// Get the ISO week number for a date
// Week 1 is the week containing the first Thursday of the year (or January 4th)
export function getWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNum = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return weekNum;
}

// Get the year for a date, considering ISO week year
// If it's late December but ISO week 1, it belongs to next year
// If it's early January but ISO week 52/53, it belongs to previous year
export function getISOWeekYear(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    return d.getUTCFullYear();
}

// Get the start and end dates for a specific week number in a year
export function getWeekDateRange(year, weekNumber) {
    // Find January 4th of the year (always in week 1 per ISO 8601)
    const jan4 = new Date(year, 0, 4);

    // Get the Monday of week 1
    const dayOfWeek = jan4.getDay() || 7; // Convert Sunday (0) to 7
    const week1Monday = new Date(jan4);
    week1Monday.setDate(jan4.getDate() - dayOfWeek + 1);

    // Calculate the Monday of the target week
    const targetMonday = new Date(week1Monday);
    targetMonday.setDate(week1Monday.getDate() + (weekNumber - 1) * 7);

    // Calculate Sunday of the target week
    const targetSunday = new Date(targetMonday);
    targetSunday.setDate(targetMonday.getDate() + 6);

    return {
        start: targetMonday,
        end: targetSunday
    };
}

// Format a date range for display (e.g., "Jan 1 - Jan 7")
export function formatWeekRange(year, weekNumber) {
    const { start, end } = getWeekDateRange(year, weekNumber);

    const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
    const startDay = start.getDate();
    const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
    const endDay = end.getDate();

    // If same month, don't repeat it
    if (startMonth === endMonth) {
        return `${startMonth} ${startDay}-${endDay}`;
    }

    return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
}

// Get all weeks a promotion spans within a specific year
export function getPromoWeeksInYear(promo, year) {
    // Parse as local midnight, not UTC midnight — a bare "YYYY-MM-DD" string
    // parses as UTC per the JS spec, which shifts the calendar date back a
    // day in any timezone behind UTC and can push a promo out of its correct
    // week (e.g. a promo ending on a Monday losing that day entirely).
    const startDate = new Date(promo.startDate + 'T00:00:00');
    const endDate = new Date(promo.endDate + 'T00:00:00');

    const weeks = [];

    // Iterate through each day of the promotion
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
        const dateYear = getISOWeekYear(currentDate);
        const weekNum = getWeekNumber(currentDate);

        // Only include if this date belongs to the specified year
        if (dateYear === year) {
            if (!weeks.includes(weekNum)) {
                weeks.push(weekNum);
            }
        }

        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return weeks.sort((a, b) => a - b);
}

// Group promotions by year and week, with promotions spanning multiple weeks
export function groupPromosByYearAndWeek(promotions, years) {
    const grouped = {};

    // Initialize all years
    years.forEach(year => {
        grouped[year] = {};
    });

    promotions.forEach(promo => {
        // For each year, find which weeks this promo spans
        years.forEach(year => {
            const weeksInYear = getPromoWeeksInYear(promo, year);

            weeksInYear.forEach(week => {
                if (!grouped[year][week]) {
                    grouped[year][week] = [];
                }

                // Avoid duplicates
                if (!grouped[year][week].find(p => p.id === promo.id)) {
                    grouped[year][week].push(promo);
                }
            });
        });
    });

    // Sort promotions within each week by start date
    Object.keys(grouped).forEach(year => {
        Object.keys(grouped[year]).forEach(week => {
            grouped[year][week].sort((a, b) =>
                new Date(a.startDate) - new Date(b.startDate)
            );
        });
    });

    return grouped;
}
